import { repairImageKitUrl } from '@dupe/common/constants'
import { ArrowUpRight } from 'lucide-react'
import Link from 'next/link'
import { buildSeoReportPath } from '@dupe/common/research-report'
import { ProgressiveBlur } from '~/src/components/ui/progressive-blur'
import { useSeoReportAnalytics } from '~/src/components/reports/SeoReportAnalyticsContext'

interface RelatedReport {
  slug: string
  title: string
  categorySlug?: string | null
  subcategorySlug?: string | null
  imageUrl?: string | null
}

interface SeoReportRelatedReportsProps {
  reports: RelatedReport[]
}

/* color-token-exception: decorative fallback gradients for cards without images; unique per-card palette, not mappable to semantic tokens */
const FALLBACK_GRADIENTS = [
  'linear-gradient(135deg, #f5c6aa 0%, #e8a87c 100%)',
  'linear-gradient(135deg, #c3a5d9 0%, #9b7fc4 100%)',
  'linear-gradient(135deg, #7db4d8 0%, #5a8fb5 100%)',
  'linear-gradient(135deg, #a8d8c5 0%, #7bc4a5 100%)',
  'linear-gradient(135deg, #f0b6c1 0%, #d98ea0 100%)',
] as const

function RelatedReportCard({
  report,
  index,
}: {
  report: RelatedReport
  index: number
}) {
  const reportAnalytics = useSeoReportAnalytics()
  const fallbackGradient = FALLBACK_GRADIENTS[index % FALLBACK_GRADIENTS.length]

  return (
    <Link
      href={buildSeoReportPath(report)}
      className='group relative flex h-[300px] w-full flex-1 items-end overflow-hidden rounded-2xl'
      onClick={() =>
        reportAnalytics?.onRelatedClick({
          targetSlug: report.slug,
          targetTitle: report.title,
          position: index,
        })
      }
    >
      {report.imageUrl ? (
        <img
          src={repairImageKitUrl(report.imageUrl)}
          alt={report.title}
          className='absolute inset-0 h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105'
          loading='lazy'
        />
      ) : (
        <div
          className='absolute inset-0 transition-transform duration-500 ease-out group-hover:scale-105'
          style={{ background: fallbackGradient }}
        />
      )}

      {/* Progressive blur overlay */}
      <ProgressiveBlur className='bottom-0' height='50%' position='bottom' />
      {/* Text overlay */}
      <div className='relative z-20 flex w-full items-end justify-between px-4 pb-4 pt-20'>
        <p className='flex-1 text-balance text-base font-bold leading-6 text-white'>
          {report.title}
        </p>
        <ArrowUpRight className='size-6 shrink-0 text-white transition-transform duration-300 ease-out group-hover:-translate-y-0.5 group-hover:translate-x-0.5' />
      </div>
    </Link>
  )
}

export function SeoReportRelatedReports({
  reports,
}: SeoReportRelatedReportsProps) {
  if (reports.length === 0) return null

  return (
    <section className='flex flex-col gap-6'>
      <h2 className='font-display text-2xl font-bold text-foreground'>
        Related reports
      </h2>

      <div className='grid grid-cols-2 gap-3 md:flex md:flex-row md:gap-3'>
        {reports.map((report, index) => (
          <div
            key={report.slug}
            className={index > 1 ? 'hidden md:flex md:flex-1' : 'flex flex-1'}
          >
            <RelatedReportCard report={report} index={index} />
          </div>
        ))}
      </div>
    </section>
  )
}
